/**
 * wave-calm.js — Ozlo calm / masking wave
 *
 * 4 clean geometric sine lines:
 * - Outer 10% left/right: lines fade out to near-zero (same idea as noise)
 * - Centre 80%: calm smooth flowing pattern
 * - Slower speeds — not rushed
 * - Amplitude reduced ~12% from previous version
 *
 * API: new OzloWaveCalm(canvas) → .start() / .stop() / .destroy()
 */
(function (global) {
  'use strict';

  class OzloWaveCalm {
    constructor(canvas) {
      this._canvas  = canvas;
      this._ctx     = canvas.getContext('2d');
      this._raf     = null;
      this._running = false;
      this._t       = 0;
      // Each line starts evenly spread: 0, π/2, π, 3π/2
      this._ph = [0, 1.5708, 3.1416, 4.7124];

      this._resize = this._resize.bind(this);
      this._ro = new ResizeObserver(this._resize);
      this._ro.observe(canvas);
      this._resize();
    }

    start() {
      if (this._running) return;
      this._running = true;
      this._loop();
    }

    stop() {
      this._running = false;
      if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; }
      this._ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
    }

    destroy() { this.stop(); this._ro.disconnect(); }

    _resize() {
      const dpr = window.devicePixelRatio || 1;
      const r   = this._canvas.getBoundingClientRect();
      this._canvas.width  = r.width  * dpr;
      this._canvas.height = r.height * dpr;
    }

    // Zone envelope: same logic as noise — edges fade to near-zero.
    // xFrac: 0..1 position across canvas width
    _zoneEnvelope(xFrac) {
      if (xFrac < 0.10)       return 0.04;
      if (xFrac < 0.20)       return 0.04 + 0.96 * ((xFrac - 0.10) / 0.10);
      if (xFrac > 0.90)       return 0.04;
      if (xFrac > 0.80)       return 0.04 + 0.96 * ((0.90 - xFrac) / 0.10);
      return 1.0;
    }

    // Pure clean sine Y — amplitude modulated by zone envelope at this x
    _lineY(x, W, H, freq, maxAmp, phase) {
      const xFrac   = x / W;
      const env     = this._zoneEnvelope(xFrac);
      const amp     = maxAmp * env;
      return H * 0.5 + Math.sin(xFrac * freq * Math.PI * 2 + phase) * amp;
    }

    _drawLine(ctx, W, H, freq, maxAmp, phase, glowSize, coreAlpha) {
      ctx.lineJoin = 'round';
      ctx.lineCap  = 'round';

      // Clean crisp line only — no glow, no shadow, no fading
      ctx.beginPath();
      ctx.lineWidth   = 1.8;
      ctx.strokeStyle = `rgba(255,255,255,${coreAlpha})`;
      for (let x = 0; x <= W; x += 2) {
        const y = this._lineY(x, W, H, freq, maxAmp, phase);
        x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.stroke();
    }

    _loop() {
      if (!this._running) return;
      const dpr = window.devicePixelRatio || 1;
      const W   = this._canvas.width  / dpr;
      const H   = this._canvas.height / dpr;
      const ctx = this._ctx;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);

      // Slow gentle breathe
      const breathe = 1 + 0.05 * Math.sin(this._t * 0.004);

      // Reduced from H*0.36 → H*0.31 (~14% reduction)
      // Speeds halved from previous version — much calmer, not rushed
      //    freq    amp         glow  alpha   speed
      const lines = [
        [ 2.000,  H * 0.28,   10,   0.55,   0.0055 ],  // slowest
        [ 2.500,  H * 0.28,   12,   0.72,   0.0070 ],
        [ 3.000,  H * 0.28,   14,   0.90,   0.0085 ],  // primary
        [ 3.618,  H * 0.28,   12,   0.72,   0.0105 ],  // golden ratio freq
      ];

      for (let i = 0; i < lines.length; i++) {
        const [freq, amp, glow, alpha, speed] = lines[i];
        this._drawLine(ctx, W, H, freq, amp * breathe, this._ph[i], glow, alpha);
        this._ph[i] += speed;
      }

      this._t += 1;
      this._raf = requestAnimationFrame(() => this._loop());
    }
  }

  global.OzloWaveCalm = OzloWaveCalm;
}(window));